import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypePostShippingLabelsCancelParams,
  TypePostShippingLabelsCancelResponse,
} from '~/types/delivery-agency.types';

/**
 * [운송장 관리] 운송장 취소 API
 */
const fetchPostShippingLabelsCancel = async (
  params: TypePostShippingLabelsCancelParams
): Promise<TypePostShippingLabelsCancelResponse['data']> => {
  const { data } = await axiosInstance.post<TypePostShippingLabelsCancelResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.POST_SHIPPING_LABELS_CANCEL,
    params
  );
  return data.data;
};

/**
 * [운송장 관리] 운송장 취소 Mutation Hook
 */
export const usePostShippingLabelsCancelMutation = () => {
  return useMutation<
    TypePostShippingLabelsCancelResponse['data'],
    TypeErrorResponseData['error'],
    TypePostShippingLabelsCancelParams
  >({
    mutationFn: fetchPostShippingLabelsCancel,
    meta: { showGlobalErrorToast: false },
  });
};

/**
 * [운송장 관리] 운송장 취소 Mock
 *
 * @remarks
 * DevTools에서 Mock을 활성화하면 응답합니다. 실패 사유에 NEED_ORDER_CANCEL 이 있으면 취소 불가 상세 모달이 열린다.
 */
registerMockAPI<TypePostShippingLabelsCancelResponse>({
  method: 'post',
  endpoint: '/api-delivery-agency/shipping-labels/cancel',
  label: '운송장 관리/배송 취소',
  scenarios: [
    { name: '전건 성공', response: { data: null } },
    {
      name: '주문취소 필요 건 포함',
      response: {
        data: {
          totalCount: 3,
          successCount: 1,
          failCount: 2,
          failureGroups: [
            {
              reason: 'NEED_ORDER_CANCEL',
              failures: [
                {
                  reason: 'NEED_ORDER_CANCEL',
                  outboundCode: 'JA9LAK0140001',
                  shippingLabelNumbers: ['210-C0-JA9LAK0140001-0001'],
                  clientOrderCodes: ['ORD-20260720-0001'],
                },
                {
                  reason: 'NEED_ORDER_CANCEL',
                  outboundCode: 'JA9LAK0140003',
                  shippingLabelNumbers: ['210-C0-JA9LAK0140003-0001'],
                  clientOrderCodes: ['ORD-20260719-0011', 'ORD-20260719-0012'],
                },
              ],
            },
          ],
        },
      },
    },
  ],
});
